import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { TranslationKeys } from 'app/translations';
import type { InputFactoryOnChangeFunc } from '../FormikField';
import { InputFieldFactory } from '../FormikField';
import { ActionButton } from '../ActionButton';
import { LinearProgressStyled, SaveButtonContainerStyled, savedPulseAnimation } from './NotesInput.styles';

const SUCCESS_ANIMATION_DURATION = 2000;

type NotesInputProps = {
  inputValue: string;
  onSaveNote: (inputValue: string) => void;
  isLoading?: boolean;
  isNotAllowed?: boolean;
};

export const NotesInput = ({ inputValue, onSaveNote, isLoading = false, isNotAllowed = false }: NotesInputProps) => {
  const [localValue, setLocalValue] = React.useState(inputValue);
  const [justSaved, setJustSaved] = React.useState(false);
  const wasSavingRef = React.useRef(false);

  React.useEffect(() => {
    setLocalValue(inputValue);
  }, [inputValue]);

  React.useEffect(() => {
    if (wasSavingRef.current && !isLoading) {
      wasSavingRef.current = false;
      setJustSaved(true);
      const timeout = setTimeout(() => setJustSaved(false), SUCCESS_ANIMATION_DURATION);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isLoading]);

  const handleChange = React.useCallback<InputFactoryOnChangeFunc>(({ value }) => {
    setLocalValue(value);
  }, []);

  const handleSave = React.useCallback(() => {
    wasSavingRef.current = true;
    onSaveNote(localValue);
  }, [localValue, onSaveNote]);

  const hasChanges = localValue !== inputValue;

  return (
    <>
      <Typography variant="h6">
        <FormattedMessage id={TranslationKeys.notes} />
      </Typography>

      <InputFieldFactory
        onChange={handleChange}
        field={{
          name: 'notes',
          type: 'text',
          multiline: true,
          minRows: 5,
          value: localValue,
          loading: isLoading,
          disabled: isNotAllowed,
        }}
      />

      {isLoading && <LinearProgressStyled color={'secondary'} />}

      <SaveButtonContainerStyled>
        <ActionButton
          onClick={handleSave}
          messageId={justSaved ? 'button.saved' : 'button.save'}
          icon={justSaved ? faCheck : undefined}
          disabled={isLoading || isNotAllowed || !hasChanges}
          sx={justSaved ? { animation: `${savedPulseAnimation} 0.4s ease-in-out` } : undefined}
        />
      </SaveButtonContainerStyled>
    </>
  );
};
